/**
 * Multi-row keyword search form with Row Groups for complex Boolean logic.
 *
 * Groups allow expressions like:
 *   (nausea AND vomiting) OR (diarrhea AND headache)
 *
 * Each group has rows + inner operator (AND/OR) + optional proximity.
 * Groups are combined by a top-level group operator (AND/OR).
 *
 * Single-group mode looks and behaves identically to the original flat UI.
 */
import React, { useReducer, useCallback, useState, useMemo, useEffect } from 'react';
import { authApi } from '../services/authApi';
import { formatKeywordQuery } from '../utils/keywordQueryText';
import { EXACT_ONLY_PLACEHOLDER, KIND_LABELS, hasLiteralCore } from './SearchChipBar';
import { canonicalRow, patternKeyword } from '../utils/keywordMatchRule';
import KeywordSuggestPopover, { buildSuggestOptions, useSearchSuggest } from './KeywordSuggestPopover';
import type {
  ChipKind,
  Corpus,
  KeywordSearchRow as SearchRow,
  KeywordSearchGroup,
  KeywordRowOperator,
  KeywordSectionOption,
  KeywordSearchSource,
  ProximitySearch,
  SearchRegion,
  SuggestRow,
} from '../types';

// ─── State ───────────────────────────────────────────────────────────────────

/** Extended row state with local synonym data for the form. */
interface RowState extends SearchRow {
  synonyms: string[] | null;
  synonymsLoading: boolean;
  /** Operator connecting this row to the previous row. Ignored for first row. */
  rowOperator: KeywordRowOperator;
}

interface GroupState {
  rows: RowState[];
  innerOperator: KeywordRowOperator;
  precedingOperator: KeywordRowOperator;
  proximityEnabled: boolean;
  proximity: ProximitySearch;
}

interface FormState {
  groups: GroupState[];
}

type Action =
  | { type: 'ADD_GROUP' }
  | { type: 'REMOVE_GROUP'; groupIndex: number }
  | { type: 'SET_PRECEDING_OPERATOR'; groupIndex: number; operator: KeywordRowOperator }
  | { type: 'SET_INNER_OPERATOR'; groupIndex: number; operator: KeywordRowOperator }
  | { type: 'SET_ROW_OPERATOR'; groupIndex: number; rowIndex: number; operator: KeywordRowOperator }
  | { type: 'ADD_ROW'; groupIndex: number }
  | { type: 'REMOVE_ROW'; groupIndex: number; rowIndex: number }
  | { type: 'UPDATE_ROW'; groupIndex: number; rowIndex: number; field: keyof SearchRow; value: string | boolean | null }
  | { type: 'TOGGLE_PROXIMITY'; groupIndex: number }
  | { type: 'UPDATE_PROXIMITY'; groupIndex: number; field: keyof ProximitySearch; value: string | number }
  | { type: 'SET_SYNONYMS_LOADING'; groupIndex: number; rowIndex: number; loading: boolean }
  | { type: 'SET_SYNONYMS'; groupIndex: number; rowIndex: number; synonyms: string[] }
  | { type: 'REMOVE_SYNONYM'; groupIndex: number; rowIndex: number; synIndex: number }
  | { type: 'ADD_SYNONYM'; groupIndex: number; rowIndex: number; synonym: string }
  | { type: 'CLEAR_SYNONYMS'; groupIndex: number; rowIndex: number }
  // Row type: a field kind (manufacturer/generic/...) or null = Label text.
  // A field kind clears section scope and synonyms (a field row has neither);
  // A suggestion pick can also set the keyword.
  | {
      type: 'SET_ROW_TYPE'; groupIndex: number; rowIndex: number; drugField: ChipKind | null;
      keyword?: string;
      /** Label-text row only: Label scope (no suggestions) rather than All. */
      labelOnly?: boolean;
    }


/** Q4: minimum normalized length of a Japanese Exact term (matches the
 *  server's MIN_EXACT_CHARS in app/services/jp_ja_text.py). */
export const JA_MIN_EXACT_CHARS = 2;

const defaultRow = (): RowState => ({
  keyword: '',
  section_scope: null,
  match_type: 'exact',
  use_synonyms: false,
  negate: false,
  synonyms: null,
  synonymsLoading: false,
  rowOperator: 'AND',
});

const defaultGroup = (): GroupState => ({
  rows: [defaultRow()],
  innerOperator: 'AND',
  precedingOperator: 'AND',
  proximityEnabled: false,
  proximity: { term_a: '', term_b: '', max_distance: 5 },
});

/** Convert API-level groups back to form state (for restoring previous search). */
function groupsToFormState(groups: KeywordSearchGroup[]): FormState {
  return {
    groups: groups.map((g) => ({
      rows: g.rows.map((r) => ({
        ...r,
        synonyms: r.custom_synonyms ?? null,
        synonymsLoading: false,
        rowOperator: g.inner_operator,
      })),
      innerOperator: g.inner_operator,
      precedingOperator: g.preceding_operator ?? 'AND',
      proximityEnabled: !!(g.proximity && g.proximity.term_a && g.proximity.term_b),
      proximity: g.proximity ?? { term_a: '', term_b: '', max_distance: 5 },
    })),
  };
}

const defaultInitialState: FormState = {
  groups: [defaultGroup()],
};

/** Helper to update a specific group immutably. */
function updateGroup(state: FormState, groupIndex: number, updater: (g: GroupState) => GroupState): FormState {
  return {
    ...state,
    groups: state.groups.map((g, i) => (i === groupIndex ? updater(g) : g)),
  };
}

/** Helper to update a specific row within a group immutably. */
function updateRow(group: GroupState, rowIndex: number, updater: (r: RowState) => RowState): GroupState {
  return {
    ...group,
    rows: group.rows.map((r, i) => (i === rowIndex ? updater(r) : r)),
  };
}

/** Turn the form's groups into the groups the backend receives. Shared by
 *  ``handleSearch`` and the QUERY line so the two cannot drift. */
function buildOutputGroups(groups: GroupState[], native: boolean, letterCorpus: boolean): KeywordSearchGroup[] {
  // Build output groups. When rows within a UI group have mixed operators
  // (e.g. row1 AND row2 OR row3), split into backend sub-groups:
  // group1(row1 AND row2) OR group2(row3).
  const output: KeywordSearchGroup[] = [];

  for (const g of groups) {
    const proximity =
      !native && !letterCorpus && g.proximityEnabled && g.proximity.term_a.trim() && g.proximity.term_b.trim()
        ? g.proximity
        : null;

    const activeRows = g.rows.filter((r) => r.keyword.trim().length > 0);
    if (activeRows.length === 0) {
      // Proximity-only group: the proximity IS the group's query (the
      // backend keeps groups with rows OR a proximity).
      if (proximity) {
        output.push({
          rows: [],
          inner_operator: g.innerOperator,
          proximity,
          preceding_operator: g.precedingOperator,
        });
      }
      continue;
    }

    // Split rows into sub-groups at operator boundaries
    const toSearchRow = (r: RowState): SearchRow => native
      ? {
          // Native text (jp_ja / de / ch_src): no section scope / synonyms (the backend 400s).
          keyword: canonicalRow(r).keyword,
          section_scope: null,
          match_type: 'exact',
          use_synonyms: false,
          custom_synonyms: null,
          negate: r.negate ?? false,
          ...(r.drug_field ? { drug_field: r.drug_field } : r.label_only ? { label_only: true } : {}),
        }
      : {
          // Canonicalize restored rows and field patterns.
          keyword: canonicalRow(r).keyword,
          section_scope: r.section_scope,
          match_type: 'exact',
          use_synonyms: r.use_synonyms,
          custom_synonyms: r.synonyms && r.synonyms.length > 0 ? r.synonyms : null,
          negate: r.negate ?? false,
          ...(r.drug_field ? { drug_field: r.drug_field } : r.label_only ? { label_only: true } : {}),
        };

    // First row starts a sub-group.
    const firstRow = activeRows[0]!;
    let currentRows: SearchRow[] = [toSearchRow(firstRow)];
    let currentOp: KeywordRowOperator = activeRows.length > 1 ? activeRows[1]!.rowOperator : 'AND';
    let isFirstSubGroup = true;

    for (let i = 1; i < activeRows.length; i++) {
      const row = activeRows[i]!;
      if (row.rowOperator === currentOp) {
        currentRows.push(toSearchRow(row));
      } else {
        // Operator changed — flush current sub-group
        output.push({
          rows: currentRows,
          inner_operator: currentOp,
          proximity: isFirstSubGroup ? proximity : null,
          preceding_operator: isFirstSubGroup ? g.precedingOperator : row.rowOperator,
        });
        isFirstSubGroup = false;
        currentRows = [toSearchRow(row)];
        currentOp = row.rowOperator;
      }
    }

    // Flush final sub-group
    output.push({
      rows: currentRows,
      inner_operator: currentOp,
      // Each UI group's proximity rides on its first sub-group (the backend
      // applies proximity per group); it used to be dropped for group 2+.
      proximity: isFirstSubGroup ? proximity : null,
      preceding_operator: isFirstSubGroup ? g.precedingOperator : currentOp,
    });
  }
  return output;
}

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    // ── Group-level actions ──
    case 'ADD_GROUP':
      if (state.groups.length >= 4) return state;
      return { ...state, groups: [...state.groups, defaultGroup()] };
    case 'REMOVE_GROUP':
      if (state.groups.length <= 1) return state;
      return { ...state, groups: state.groups.filter((_, i) => i !== action.groupIndex) };
    case 'SET_PRECEDING_OPERATOR':
      return updateGroup(state, action.groupIndex, (g) => ({ ...g, precedingOperator: action.operator }));
    case 'SET_INNER_OPERATOR':
      return updateGroup(state, action.groupIndex, (g) => ({ ...g, innerOperator: action.operator }));
    case 'SET_ROW_OPERATOR':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => ({ ...r, rowOperator: action.operator }))
      );

    // ── Row-level actions ──
    case 'ADD_ROW':
      return updateGroup(state, action.groupIndex, (g) => {
        if (g.rows.length >= 10) return g;
        const row = defaultRow();
        return { ...g, rows: [...g.rows, row] };
      });
    case 'REMOVE_ROW':
      return updateGroup(state, action.groupIndex, (g) => {
        if (g.rows.length <= 1) return g;
        return { ...g, rows: g.rows.filter((_, i) => i !== action.rowIndex) };
      });
    case 'UPDATE_ROW':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => {
          const updated = { ...r, [action.field]: action.value };
          if (action.field === 'keyword') {
            updated.synonyms = null;
            updated.use_synonyms = false;
          }
          return updated;
        })
      );

    // ── Proximity actions ──
    case 'TOGGLE_PROXIMITY':
      return updateGroup(state, action.groupIndex, (g) => ({
        ...g,
        proximityEnabled: !g.proximityEnabled,
      }));
    case 'UPDATE_PROXIMITY':
      return updateGroup(state, action.groupIndex, (g) => ({
        ...g,
        proximity: { ...g.proximity, [action.field]: action.value },
      }));

    // ── Synonym actions ──
    case 'SET_SYNONYMS_LOADING':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => ({ ...r, synonymsLoading: action.loading }))
      );
    case 'SET_SYNONYMS':
      return updateGroup(state, action.groupIndex, (g) =>
        // A response that lands after the row became a field row is stale:
        // field rows never carry synonyms (the backend 422s them).
        updateRow(g, action.rowIndex, (r) => r.drug_field ? { ...r, synonymsLoading: false } : ({
          ...r,
          synonyms: action.synonyms,
          synonymsLoading: false,
          use_synonyms: action.synonyms.length > 0,
        }))
      );
    case 'REMOVE_SYNONYM':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => {
          if (!r.synonyms) return r;
          const newSyns = r.synonyms.filter((_, si) => si !== action.synIndex);
          return {
            ...r,
            synonyms: newSyns.length > 0 ? newSyns : null,
            use_synonyms: newSyns.length > 0,
          };
        })
      );
    case 'ADD_SYNONYM':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => {
          const syns = r.synonyms ?? [];
          return { ...r, synonyms: [...syns, action.synonym], use_synonyms: true };
        })
      );
    case 'CLEAR_SYNONYMS':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => ({
          ...r,
          synonyms: null,
          use_synonyms: false,
        }))
      );
    case 'SET_ROW_TYPE':
      return updateGroup(state, action.groupIndex, (g) =>
        updateRow(g, action.rowIndex, (r) => ({
          ...r,
          drug_field: action.drugField,
          label_only: !action.drugField && !!action.labelOnly,
          ...(action.drugField
            ? { section_scope: null, use_synonyms: false, synonyms: null, custom_synonyms: null }
            : {}),
          ...(action.keyword !== undefined ? { keyword: action.keyword } : {}),
        }))
      );

    default:
      return state;
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface KeywordSearchFormProps {
  /** Page-level corpus toggle.
   *
   *  - ``'labels'``      → user picks any subset of ``{label, sba, epar}``.
   *    The SBA/EPAR multi-select sub-toggles render normally.
   *  - ``'fda_letters'`` → sources are force-locked to ``['fda_letter']``
   *    and the SBA/EPAR multi-select sub-toggles are hidden (not just
   *    disabled — fully removed from the DOM).  Backend dispatches to
   *    the letter-shaped response, which ``SearchPage`` renders via
   *    ``LetterResultsTable``.
   */
  corpus: Corpus;
  sectionOptions: KeywordSectionOption[];
  isLoading?: boolean;
  /** Restore form state from a previous search (e.g. after back-navigation). */
  initialGroups?: KeywordSearchGroup[] | null | undefined;
  /** No longer read: the Japanese route is chosen from the typed keywords,
   *  so a replayed jp_ja search re-detects it. Kept so callers (SearchPage
   *  replay) and the sourceRouting test still compile. */
  initialSources?: KeywordSearchSource[] | null | undefined;
  /**
   * Region codes currently selected in the page-level region filter.
   * The form dispatches these to the right per-region main-label source set
   * on submit.  Passed in as a prop (rather than read via context) so the form
   * stays decoupled from filter state ownership.
   */
  selectedRegions?: SearchRegion[];
  /** Admin "origin version scope" is on (US origin labels only). Japanese-text
   *  search can't combine with it (the backend refuses), so the toggle is
   *  shown disabled. Optional: every other caller behaves as before. */
  originScope?: boolean | undefined;
  onSearch: (groups: KeywordSearchGroup[], sources: KeywordSearchSource[]) => void;
  /** The unified search bar's scope dropdown (KeywordSurface). When given,
   *  each row renders it fused to the left of its input INSTEAD of the per-row
   *  section <select>; its section choice is that row's ``section_scope``.
   *  Optional so every other caller renders the form exactly as before. */
  scopeDropdown?: ((ctx: ScopeDropdownCtx) => React.ReactNode) | undefined;
  /** Live application-category filter keys; feeds the QUERY line only. */
  marketingCategories?: string[] | undefined;
  /** The host's filter bar, rendered inside the card between the builder and
   *  the QUERY bar. */
  filtersSlot?: React.ReactNode;
  /** Suggestion popover on the focused row: an All row gets the grouped
   *  popover with a "Search labels" row, a field row only its kind; a pick
   *  sets that row's type/value/match type. A Label-scope row gets none. Undefined (CEPI, kill switch) =
   *  no popover and no suggest calls. */
  suggest?: { regions?: string[] } | undefined;
  /** Row 0 starts with this field type (legacy ``?kwInput=<kind>`` link).
   *  Read once, with the initial state; ignored when ``initialGroups`` is set. */
  initialRowType?: ChipKind | undefined;
}

/** All-scope popover is suppressed for text with query syntax. */
const QUERY_SYNTAX_RE = /["*]|\s(AND|OR|NOT)\s/i;

/** What the form hands the host's scope dropdown for each row. */
export interface ScopeDropdownCtx {
  sectionOptions: KeywordSectionOption[];
  section: string | null;
  onSectionChange: (sectionId: string | null) => void;
  sectionDisabled: boolean;
  sectionDisabledTitle: string | undefined;
  disabled: boolean;
  /** This row's field type; null = label text (All, or Label when ``labelOnly``). */
  rowType: ChipKind | null;
  labelOnly: boolean;
  onRowTypeChange: (type: ChipKind | null, labelOnly?: boolean) => void;
  /** Field types are offered (not letters corpus / Japanese text / origin). */
  fieldTypesEnabled: boolean;
}

// ─── Region → source dispatcher (the regions → sources transformation lives
//     here so SearchPage doesn't need to know).
//
//     For each region the user picked, add the corpus holding that region's
//     MAIN LABEL.  Multi-region selections naturally union (US + EU →
//     ['label', 'eu']; US + AU → ['label', 'au_pi'], since AU's and EU's main
//     labels live outside the sections table).
//
//     The "Other Documents" (ha_reviews) branch was REMOVED on 2026-07-29 —
//     keyword search covers main labels only.  See
//     docs/superpowers/specs/2026-07-29-keyword-search-remove-ha-documents-design.md
//
//     Can still return an empty array: region='CUSTOM' has no main-label
//     corpus.  The caller falls back to ['label'] so we never send an empty
//     sources list to the min_length=1 backend schema.
// ─────────────────────────────────────────────────────────────────────
export function selectLabelSources(regions: SearchRegion[]): KeywordSearchSource[] {
  const out = new Set<KeywordSearchSource>();
  const hasRegion = (r: SearchRegion) => regions.includes(r);
  // No region filter == "all regions"; we treat that as the union of every
  // region's main-label corpus.  Matches the existing backend behaviour where
  // an absent ``regions`` field is the wildcard.
  const all = regions.length === 0;

  // US/UK main label live in the sections table — one source covers both.
  // EU migrated OUT of sections to its own eu_evidence_chunks corpus, so it
  // dispatches the 'eu' source (mirrors CA→ca_pm, AU→au_pi).
  if (all || hasRegion('US') || hasRegion('UK')) {
    out.add('label');
  }
  if (all || hasRegion('CA')) out.add('ca_pm');
  if (all || hasRegion('AU')) out.add('au_pi');
  if (all || hasRegion('EU')) out.add('eu');
  // JP PMDA labels live in jp_evidence_chunks → 'jp' sidecar corpus
  // (English/translated text only). Mirrors EU→eu, CA→ca_pm, AU→au_pi.
  if (all || hasRegion('JP')) out.add('jp');
  // CH (Swiss English text) launched to all users 2026-10-27 → part of "all".
  // IE: only when picked EXPLICITLY — still admin-only on the backend
  // (RestrictedSource -> 403), so including it in a non-admin's all-regions
  // search would fail the whole request.
  if (all || hasRegion('CH')) out.add('ch');
  if (hasRegion('IE')) out.add('ie');
  // DE / FR / IT: the German / French / Italian ORIGINAL (native corpora 'de' /
  // 'fr' / 'it'; an English query is translated server-side) — searched ALONE
  // (the backend refuses a native corpus next to any other), so only when DE,
  // FR or IT is the ONLY region picked.
  if (regions.length === 1 && regions[0] === 'DE') out.add('de');
  if (regions.length === 1 && regions[0] === 'FR') out.add('fr');
  if (regions.length === 1 && regions[0] === 'IT') out.add('it');
  // US-Drugs@FDA label PDFs are only ever an EXPLICIT choice (D10): an empty
  // region list (= "all") never includes them.
  if (hasRegion('US_FDA')) out.add('fda_label_pdf');

  return Array.from(out);
}

/** Kana or kanji anywhere in ``kw`` (incl. half-width katakana and kanji
 *  outside the BMP). Decides the Japanese keyword route when JP is the only
 *  region; Latin, digits and full-width Latin stay on the English route. */
export function hasJapaneseText(kw: string): boolean {
  return /[\u3040-\u30FF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF66-\uFF9F]/.test(kw)
    || Array.from(kw).some((ch) => {
      const cp = ch.codePointAt(0) ?? 0;
      return cp >= 0x20000 && cp <= 0x3134f;
    });
}

/** A Latin letter outside ASCII (ä ö ü ß é è à ç œ …). With CH as the only
 *  region, a keyword containing one searches the Swiss ORIGINAL text
 *  ('ch_src') instead of the English translation ('ch'). */
export function hasLatinAccent(kw: string): boolean {
  return /[\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u017F\u1E9E]/.test(kw);
}

/** True while an IME composition is active: the keystroke belongs to the
 *  input method (e.g. Enter commits 間質性肺炎), not to the form. keyCode 229
 *  covers browsers (Safari) that end the composition before keydown fires. */
export function isImeComposing(e: React.KeyboardEvent): boolean {
  return (e.nativeEvent as KeyboardEvent).isComposing === true || e.keyCode === 229;
}

// ─── Component ───────────────────────────────────────────────────────────────

// (``_SUPPLEMENTARY_SOURCES`` / ``_modeFromSources`` lived here.  They mapped a
// restored ``initialSources`` array back onto the Labels / Other Documents
// toggle.  Both were deleted with that toggle on 2026-07-29 — the form is
// labels-only now, so persisted sources no longer select a mode.
//
// Deleting ``_modeFromSources`` is the load-bearing half of that removal:
// without it, a replayed pre-removal search could put the form back into a mode
// with no visible control to leave, and the next submit would dispatch all six
// supplementary sources.)

const KeywordSearchForm: React.FC<KeywordSearchFormProps> = ({
  corpus,
  sectionOptions,
  isLoading = false,
  initialGroups,
  selectedRegions = [],
  originScope = false,
  onSearch,
  scopeDropdown,
  marketingCategories,
  filtersSlot,
  suggest,
  initialRowType,
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    null,
    () => initialGroups && initialGroups.length > 0
      ? groupsToFormState(initialGroups)
      : initialRowType
      ? { groups: [{ ...defaultGroup(), rows: [{ ...defaultRow(), drug_field: initialRowType }] }] }
      : defaultInitialState
  );
  const [addSynonymInput, setAddSynonymInput] = useState<Record<string, string>>({});

  // Pure FDA-Letters corpus runs a stemmed-only FTS over OCR'd letter
  // chunks (LetterKeywordSearchService) — it does NOT support exact match,
  // proximity, Boolean groups, or section scope, and the backend 400s if a
  // request carries them.  So we hide/disable those controls in this mode
  // instead of letting the user pick something that errors out.
  const letterCorpus = corpus === 'fda_letters';

  // ── Japanese-text search (jp_ja, plan 2026-10-09 §13) ───────────────
  // Chosen from what the user TYPED, with no switch in the UI (senior,
  // 2026-10-11): JP as the ONLY region and every keyword containing kana /
  // kanji → sources=['jp_ja'] (the Japanese original, searched alone).
  // Otherwise → the normal keyword search (['jp'] for JP = the English
  // translation), exactly as before. Any other region selection never uses it.
  const [helpOpen, setHelpOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const closeSuggest = useCallback(() => {
    setSuggestOpen(false);
    setHighlight(-1);
  }, []);

  const jpOnlyRegion = selectedRegions.length === 1 && selectedRegions[0] === 'JP';
  const jaCapable = jpOnlyRegion && !letterCorpus && !originScope;
  // Field rows are not text: Japanese detection and the typed-row count look
  // at text rows only.
  const typedRows = state.groups.flatMap((g) => g.rows).filter((r) => r.keyword.trim() && !r.drug_field);
  const jaTyped = typedRows.filter((r) => hasJapaneseText(r.keyword));
  const jpJa = jaCapable && jaTyped.length > 0 && jaTyped.length === typedRows.length;
  // Native German / French / Italian / Swiss-original search (latin_v1 corpora):
  // DE, FR or IT alone always searches the original; CH alone does when a keyword has an accented
  // letter. Same backend rules as jp_ja (searched alone; no synonyms, section
  // scope, proximity, field rows and wildcards).
  const soleRegion = selectedRegions.length === 1 && !letterCorpus && !originScope ? selectedRegions[0] : null;
  const latinNative: KeywordSearchSource | null = soleRegion === 'DE' ? 'de'
    : soleRegion === 'FR' ? 'fr'
    : soleRegion === 'IT' ? 'it'
    : soleRegion === 'CH' && typedRows.some((r) => hasLatinAccent(r.keyword)) ? 'ch_src'
    : null;
  const nativeText = jpJa || latinNative !== null;
  const nativeName = jpJa ? 'Japanese terms' : 'native-language search';
  // Field types (Manufacturer / Generic / ...) are not offered here, and an
  // existing field row with text blocks Search rather than being dropped.
  const fieldTypesBlocked = letterCorpus || nativeText || originScope;
  const fieldRowsTyped = state.groups.flatMap((g) => g.rows)
    .filter((r) => r.drug_field && r.keyword.trim());
  const fieldRowBlocked = fieldTypesBlocked && fieldRowsTyped.length > 0;
  const noLiteralCore = fieldRowsTyped.some((r) => r.keyword.includes('*') && !hasLiteralCore(r.keyword));
  // Suggest popover follows the FOCUSED row. Gated on ``suggestOpen`` (set
  // only by typing) so restored/replayed text never fires a request on mount.
  // A Label row is suppressed for query syntax, letters and Japanese text; a
  // field row suggests for any non-empty text (pattern chips need ``*``).
  const [focused, setFocused] = useState<{ g: number; r: number } | null>(null);
  const focusedRow = focused ? state.groups[focused.g]?.rows[focused.r] : undefined;
  const suggestText = focusedRow?.keyword ?? '';
  const suggestKind = focusedRow?.drug_field ?? null;
  const suggestSuppressed = !suggest || !focusedRow || fieldTypesBlocked
    || !!focusedRow.label_only
    || (suggestKind === null && QUERY_SYNTAX_RE.test(suggestText));
  const suggestQ = suggestOpen && !suggestSuppressed ? suggestText.trim() : '';
  const suggestState = useSearchSuggest(suggestQ, suggest?.regions);
  const suggestGroups = useMemo(
    () => buildSuggestOptions(suggestState.rows, suggestKind),
    [suggestState.rows, suggestKind],
  );
  const suggestFlat = suggestGroups.flatMap((g) => g.rows);
  const popoverVisible = suggestQ !== ''
    && (suggestKind === null || suggestFlat.length > 0 || suggestState.status === 'done');
  // The option set changed under the highlight: it no longer points at what
  // the user moved onto.
  useEffect(() => { setHighlight(-1); }, [suggestState.rows]);
  const optionCount = suggestFlat.length + (suggestKind === null ? 1 : 0);

  // Japanese and English rows together can't run as one search.
  const jpMixedLanguage = jaCapable && jaTyped.length > 0 && jaTyped.length < typedRows.length;
  // No wildcards: `*`, or anything NFKC turns into one (`＊`, `﹡`), the
  // same check the server makes after NFKC.
  const hasWildcard = (kw: string) => kw.normalize('NFKC').includes('*');
  // Q4: Japanese Exact needs at least JA_MIN_EXACT_CHARS characters after
  // normalization (the server refuses shorter terms).
  const tooShortForExact = (kw: string) => {
    const t = kw.normalize('NFKC').replace(/\s+/g, '');
    return t.length > 0 && Array.from(t).length < JA_MIN_EXACT_CHARS;
  };
  // What is wrong with a row in Japanese mode (null = fine). Shown under the
  // row; Search is disabled only while some row has one.
  const jpJaRowIssue = (r: { keyword: string }): 'wildcard' | 'short' | null => {
    if (!nativeText || !r.keyword.trim()) return null;
    if (hasWildcard(r.keyword)) return 'wildcard';
    if (tooShortForExact(r.keyword)) return 'short';
    return null;
  };
  const jpJaInvalid = jpMixedLanguage
    || (nativeText && state.groups.some((g) => g.rows.some((r) => jpJaRowIssue(r) !== null)));

  // Letters have no label section taxonomy, so the per-row Section dropdown is
  // disabled for that corpus.  (A second trigger — the "Other Documents" mode,
  // whose corpora had no shared taxonomy either — was removed 2026-07-29.)
  // Japanese-text search has no section scope in V1 (spec §10.1).
  const sectionScopeDisabled = letterCorpus || nativeText;
  const sectionDisabledTitle = nativeText
    ? `Section scope is not available for ${nativeName}`
    : sectionScopeDisabled
    ? 'Section scope is not available for FDA Enforcement Letters — OCR\'d letter '
      + 'chunks have no label section taxonomy'
    : undefined;

  // The per-row Section dropdown only offers sections for the REGIONS the user
  // has selected: US → US sections only; US+EU → US and EU; etc. The catalog
  // tags every option with its region, so we filter by ``selectedRegions``. An
  // empty selection means "all regions" (the page-level wildcard), so show all.
  const visibleSectionOptions = useMemo(() => {
    if (!selectedRegions || selectedRegions.length === 0) return sectionOptions;
    const picked = new Set<string>(selectedRegions);
    return sectionOptions.filter((s) => picked.has(s.region));
  }, [sectionOptions, selectedRegions]);

  // When the region selection changes, any row whose ``section_scope`` is no
  // longer offered (e.g. a US §5 scope after switching to EU-only) is reset to
  // "Full Label" — otherwise a stale, now-invalid section would silently ride
  // along to the backend and return nothing.
  useEffect(() => {
    // Don't touch scopes until the catalog has loaded — an empty options list
    // would wrongly clear a section scope restored from a saved/replayed search.
    if (sectionOptions.length === 0) return;
    const valid = new Set<string>(visibleSectionOptions.map((s) => s.section_id));
    state.groups.forEach((g, groupIndex) =>
      g.rows.forEach((r, rowIndex) => {
        if (r.section_scope && !valid.has(r.section_scope)) {
          dispatch({ type: 'UPDATE_ROW', groupIndex, rowIndex, field: 'section_scope', value: null });
        }
      }),
    );
  }, [visibleSectionOptions, sectionOptions, state]);

  const hasMultipleGroups = state.groups.length > 1;

  // ── D6 / D2 gating ──────────────────────────────────────────────────
  // anyNegated: at least one row across all groups has negate===true.
  // anyOR: at least one per-row operator is OR, or any group
  //   inner/preceding operator is OR.  These two booleans gate each other
  //   so the form can never emit a body the backend D6 validator rejects
  //   (negated rows must combine with AND only).
  const anyNegated = state.groups.some((g) => g.rows.some((r) => r.negate === true));
  const anyOR =
    state.groups.some((g) =>
      g.rows.some((r) => r.rowOperator === 'OR') ||
      g.innerOperator === 'OR' ||
      g.precedingOperator === 'OR'
    );

  const handleSearch = useCallback(() => {
    closeSuggest();   // also cancels a pending debounced suggest
    if (jpJaInvalid || fieldRowBlocked || noLiteralCore) return;   // the hint under the row explains why

    const output = buildOutputGroups(state.groups, nativeText, letterCorpus);
    if (output.length === 0) return;   // no keyword and no complete proximity

    // Corpus-aware source resolution:
    //   - 'fda_letters' → force ['fda_letter'] (page-level Document Type;
    //     the backend dispatches on ``sources: ['fda_letter']`` to the
    //     letter-shaped response).
    //   - 'labels' corpus → expand selectedRegions into concrete main-label
    //     source keys via selectLabelSources.  Falls back to ['label'] if the
    //     expansion is empty (region='CUSTOM' has no main-label corpus) so we
    //     never send an empty sources list to the min_length=1 backend schema.
    let outgoingSources: KeywordSearchSource[];
    if (corpus === 'fda_letters') {
      outgoingSources = ['fda_letter'];
    } else if (jpJa) {
      // Japanese original text — searched alone (spec §10.1).
      outgoingSources = ['jp_ja'];
    } else if (latinNative) {
      outgoingSources = [latinNative];   // native text, searched alone
    } else {
      const expanded = selectLabelSources(selectedRegions);
      outgoingSources = expanded.length > 0 ? expanded : ['label'];
    }
    onSearch(output, outgoingSources);
  }, [state, selectedRegions, onSearch, corpus, jpJa, latinNative, letterCorpus, jpJaInvalid, fieldRowBlocked, noLiteralCore, closeSuggest]);

  // ─── QUERY line (display-only; built from what handleSearch would send) ──
  const outputGroups = useMemo(
    () => buildOutputGroups(state.groups, nativeText, letterCorpus),
    [state.groups, nativeText, letterCorpus],
  );
  const queryLine = useMemo(
    () => formatKeywordQuery(outputGroups, {
      corpus,
      jpJa,
      regions: selectedRegions ?? [],
      marketingCategories: marketingCategories ?? [],
    }),
    [outputGroups, jpJa, corpus, selectedRegions, marketingCategories],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // An IME (Japanese / Chinese / Korean input) uses Enter to COMMIT the
      // composed text; that Enter must not submit the search.
      if (isImeComposing(e)) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  // A pick sets the focused row kind and value.
  const pickSuggestRow = (row: SuggestRow) => {
    if (!focused) return;
    if (row.pattern && !hasLiteralCore(row.value)) return;
    // A bare-text pattern means "contains": show it as ``*value*`` (a terminal
    // ``+`` as ``value*``).
    const value = row.pattern ? patternKeyword(row.value) : row.value;
    dispatch({
      type: 'SET_ROW_TYPE', groupIndex: focused.g, rowIndex: focused.r,
      drugField: row.kind, keyword: value,
    });
    closeSuggest();
  };

  const handleSuggestKeyDown = (e: React.KeyboardEvent) => {
    if (isImeComposing(e)) return;
    if (popoverVisible && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setHighlight((h) => (e.key === 'ArrowDown'
        ? (h + 1) % optionCount
        : (h <= 0 ? optionCount - 1 : h - 1)));
    } else if (e.key === 'Escape') {
      closeSuggest();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const offset = suggestKind === null ? 1 : 0;
      const picked = popoverVisible && highlight >= offset ? suggestFlat[highlight - offset] : undefined;
      if (picked) pickSuggestRow(picked);
      else handleSearch();
    }
  };

  const handleGenerateSynonyms = useCallback(
    async (groupIndex: number, rowIndex: number) => {
      const keyword = state.groups[groupIndex]?.rows[rowIndex]?.keyword?.trim();
      if (!keyword) return;
      dispatch({ type: 'SET_SYNONYMS_LOADING', groupIndex, rowIndex, loading: true });
      try {
        const response = await authApi.expandSynonyms(keyword);
        dispatch({ type: 'SET_SYNONYMS', groupIndex, rowIndex, synonyms: response.synonyms ?? [] });
      } catch {
        dispatch({ type: 'SET_SYNONYMS_LOADING', groupIndex, rowIndex, loading: false });
      }
    },
    [state.groups]
  );

  const handleAddCustomSynonym = useCallback(
    (groupIndex: number, rowIndex: number) => {
      const key = `${groupIndex}-${rowIndex}`;
      const value = (addSynonymInput[key] ?? '').trim();
      if (!value) return;
      dispatch({ type: 'ADD_SYNONYM', groupIndex, rowIndex, synonym: value });
      setAddSynonymInput((prev) => ({ ...prev, [key]: '' }));
    },
    [addSynonymInput]
  );

  // ─── Render a single row ─────────────────────────────────────────────

  const renderRow = (row: RowState, groupIndex: number, rowIndex: number, group: GroupState) => {
    const synKey = `${groupIndex}-${rowIndex}`;
    const isScopeRow = !!scopeDropdown;
    const isSuggestRow = !!suggest;   // every row can suggest; the popover follows focus
    const isFocusedRow = isSuggestRow && focused?.g === groupIndex && focused?.r === rowIndex;
    const isFieldRow = !!row.drug_field;
    return (
      <div key={rowIndex}>
        {/* Per-row operator toggle (AND/OR) between rows within a group.
            D6: disabled when any row is negated (negated rows require AND). */}
        {rowIndex > 0 && (
          <div className="flex justify-center my-1.5">
            <button
              onClick={() =>
                dispatch({
                  type: 'SET_ROW_OPERATOR',
                  groupIndex,
                  rowIndex,
                  operator: row.rowOperator === 'AND' ? 'OR' : 'AND',
                })
              }
              disabled={isLoading || anyNegated}
              title={anyNegated ? 'Exclude conditions combine with AND.' : undefined}
              className="font-mono px-3 py-0.5 text-xs font-bold rounded-full border border-accent-ring bg-accent-tint text-accent-deep hover:bg-accent-ring transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {row.rowOperator}
            </button>
          </div>
        )}

        {/* Row controls */}
        <div className="flex items-center gap-2">
          {/* Unified bar: each row carries the scope dropdown fused to its input. */}
          <div className="relative flex flex-1 min-w-0 items-stretch">
          {isScopeRow && scopeDropdown!({
            sectionOptions: visibleSectionOptions,
            section: sectionScopeDisabled ? null : (row.section_scope || null),
            onSectionChange: (id) =>
              dispatch({ type: 'UPDATE_ROW', groupIndex, rowIndex, field: 'section_scope', value: id }),
            sectionDisabled: sectionScopeDisabled,
            sectionDisabledTitle: sectionDisabledTitle,
            disabled: isLoading,
            rowType: row.drug_field ?? null,
            labelOnly: !!row.label_only,
            onRowTypeChange: (t, labelOnly) =>
              dispatch({ type: 'SET_ROW_TYPE', groupIndex, rowIndex, drugField: t, ...(labelOnly ? { labelOnly } : {}) }),
            fieldTypesEnabled: !fieldTypesBlocked,
          })}
          <input
            type="text"
            value={row.keyword}
            onChange={(e) => {
              dispatch({ type: 'UPDATE_ROW', groupIndex, rowIndex, field: 'keyword', value: e.target.value });
              if (isSuggestRow) {
                setFocused({ g: groupIndex, r: rowIndex });
                setSuggestOpen(true);
                setHighlight(-1);
              }
            }}
            onFocus={isSuggestRow ? () => setFocused({ g: groupIndex, r: rowIndex }) : undefined}
            onKeyDown={isSuggestRow ? handleSuggestKeyDown : handleKeyDown}
            onBlur={isSuggestRow
              // Delayed so a click on a suggestion registers first.
              ? () => { setTimeout(closeSuggest, 100); }
              : undefined}
            lang={hasJapaneseText(row.keyword) ? 'ja' : undefined}
            aria-invalid={jpJaRowIssue(row) ? true : undefined}
            aria-describedby={jpJaRowIssue(row) ? `kw-hint-${groupIndex}-${rowIndex}` : undefined}
            placeholder={row.drug_field && EXACT_ONLY_PLACEHOLDER[row.drug_field]
              ? EXACT_ONLY_PLACEHOLDER[row.drug_field]
              : row.drug_field
              ? `${KIND_LABELS[row.drug_field]} contains… (* = wildcard)`
              : jaCapable
              ? (rowIndex === 0 && groupIndex === 0 ? 'Enter keyword or phrase (English or 日本語)...' : 'Additional keyword...')
              : (rowIndex === 0 && groupIndex === 0 ? 'Enter keyword or phrase...' : 'Additional keyword...')}
            disabled={isLoading}
            className={`flex-1 min-w-0 h-[42px] px-3 text-[15px] border border-line-input focus:outline-none focus:ring-2 focus:ring-accent-ring focus:border-accent disabled:opacity-50 ${
              isScopeRow ? 'rounded-r-ctl' : 'rounded-ctl'
            } ${row.negate ? 'text-signal' : 'text-ink'}`}
          />
          {groupIndex === 0 && rowIndex === 0 && <>
            <button type="button" aria-label="How keyword matching works" aria-expanded={helpOpen} onClick={() => setHelpOpen(!helpOpen)}>ⓘ</button>
            {helpOpen && <div role="note" className="absolute z-20 top-full right-0 mt-1 w-96 max-w-full p-3 text-sm bg-white border border-line-input shadow-lg">
              <strong>How keyword matching works.</strong> Results contain your words next to each other, in the order you typed them.
              <ul>
                <li>Upper and lower case don't matter.</li>
                <li>Most punctuation is ignored: ocular/toxicity = ocular toxicity. Hyphens count as spaces: repeat-dose = repeat dose, anti-PD-1 = anti PD 1.</li>
                <li>Periods matter in numbers and section references: 0.5 does not match 05 or 10.5, and 5.1 does not match 5.12.</li>
                <li>Whole words only: ocular does not match binocular.</li>
                <li>No word endings are added: keratopathy does not match keratopathies. Add a * (keratopath*) or list variants as synonyms.</li>
                <li>Words in another order or further apart: use Proximity (renal within 5 words of impairment), or add separate rows combined with AND.</li>
              </ul>
            </div>}
          </>}
          {isFocusedRow && popoverVisible && (
            <KeywordSuggestPopover
              text={suggestText.trim()}
              kind={suggestKind}
              groups={suggestGroups}
              status={suggestState.status}
              highlight={highlight}
              onPickLabels={handleSearch}
              onPickRow={pickSuggestRow}
            />
          )}
          </div>

          <>
          {!isScopeRow && !isFieldRow && <select
            value={sectionScopeDisabled ? '' : (row.section_scope || '')}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_ROW',
                groupIndex,
                rowIndex,
                field: 'section_scope',
                value: e.target.value || null,
              })
            }
            disabled={isLoading || sectionScopeDisabled}
            title={sectionDisabledTitle}
            className="w-44 h-[42px] px-2 text-sm text-ink border border-line-input rounded-ctl bg-white focus:outline-none focus:ring-2 focus:ring-accent-ring focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">{sectionScopeDisabled && !nativeText ? 'Full doc' : 'Full Label'}</option>
            {!sectionScopeDisabled && visibleSectionOptions.map((s) => (
              <option key={s.key} value={s.section_id}>
                {s.label}
              </option>
            ))}
          </select>}

          {/* Include / Exclude toggle (D2: disabled for fda_letter-only corpus;
              D6: disabled when any OR operator is active form-wide). */}
          {(() => {
            const negateDisabled =
              isLoading ||
              letterCorpus ||
              // D6: can't set Exclude while any OR is active
              (!row.negate && anyOR);
            const negateTitle = letterCorpus
              ? 'Exclude is not supported for FDA-letter-only search'
              : !row.negate && anyOR
              ? 'Exclude conditions combine with AND — remove OR operators first'
              : row.negate
              ? 'Exclude: this keyword must NOT appear'
              : 'Include: this keyword must appear';
            return (
              <div
                className="flex border border-line-input rounded-ctl overflow-hidden"
                title={negateTitle}
              >
                {([false, true] as boolean[]).map((isNegate) => (
                  <button
                    key={String(isNegate)}
                    onClick={() =>
                      dispatch({
                        type: 'UPDATE_ROW',
                        groupIndex,
                        rowIndex,
                        field: 'negate',
                        value: isNegate,
                      })
                    }
                    disabled={negateDisabled && isNegate !== row.negate}
                    className={`h-10 px-3 text-[13px] ${
                      (row.negate ?? false) === isNegate
                        ? 'bg-ink text-ground'
                        : 'bg-white text-ink-2 hover:text-ink'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {isNegate ? 'Exclude' : 'Include'}
                  </button>
                ))}
              </div>
            );
          })()}

          {!isFieldRow && <button
            onClick={() => handleGenerateSynonyms(groupIndex, rowIndex)}
            disabled={isLoading || row.synonymsLoading || !row.keyword.trim() || nativeText}
            title={nativeText ? `Synonyms are not available for ${nativeName}` : 'Generate synonym / near-match terms'}
            className={`h-[42px] px-3 text-[13px] rounded-ctl border transition-colors flex items-center gap-1 whitespace-nowrap ${
              row.synonyms && row.synonyms.length > 0
                ? 'border-accent bg-accent-tint text-accent-deep'
                : 'border-line-input bg-white text-ink-2 hover:text-ink'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {row.synonymsLoading && (
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            + Synonyms
          </button>}

          {group.rows.length > 1 && (
            <button
              onClick={() => dispatch({ type: 'REMOVE_ROW', groupIndex, rowIndex })}
              disabled={isLoading}
              className="p-1.5 text-ink-3 hover:text-signal rounded-ctl transition-colors"
              title="Remove row"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          </>
        </div>

        {isFieldRow && row.keyword.trim() && (fieldTypesBlocked || (row.keyword.includes('*') && !hasLiteralCore(row.keyword))) && (
          <p
            aria-live="polite"
            className="mt-1 ml-1 text-xs text-warn-ink"
            data-testid="field-row-hint"
          >
            {fieldTypesBlocked
              ? `${KIND_LABELS[row.drug_field!]} rows aren't available for this search. Switch this row to Label.`
              : 'Add at least one letter or digit.'}
          </p>
        )}

        {(() => {
          const issue = jpJaRowIssue(row);
          if (!issue) return null;
          return (
            <p
              id={`kw-hint-${groupIndex}-${rowIndex}`}
              aria-live="polite"
              className="mt-1 ml-1 flex flex-wrap items-center gap-x-1 text-xs text-warn-ink"
              data-testid="jp-ja-row-hint"
            >
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {issue === 'short' ? (
                <>
                  <span>Exact needs at least {JA_MIN_EXACT_CHARS} characters.</span>
                  <span>or type a longer term.</span>
                </>
              ) : (
                <span>
                  Wildcards (*) aren't used in {jpJa ? 'Japanese' : 'native-language'} search: Exact already finds any text
                  containing these characters. Remove the *.
                </span>
              )}
            </p>
          );
        })()}

        {/* Synonym chips */}
        {!isFieldRow && row.synonyms && row.synonyms.length > 0 && (
          <div className="mt-1.5 ml-1 flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-accent-deep font-medium">Also searching:</span>
            {row.synonyms.map((syn, si) => (
              <span
                key={si}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-accent-tint text-accent-deep rounded-full border border-accent-ring"
              >
                {syn}
                <button
                  onClick={() => dispatch({ type: 'REMOVE_SYNONYM', groupIndex, rowIndex, synIndex: si })}
                  className="text-accent hover:text-accent-deep transition-colors"
                  title="Remove"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              type="text"
              value={addSynonymInput[synKey] ?? ''}
              onChange={(e) =>
                setAddSynonymInput((prev) => ({ ...prev, [synKey]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (isImeComposing(e)) return;
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSynonym(groupIndex, rowIndex);
                }
              }}
              placeholder="+ add term"
              className="w-24 px-1.5 py-0.5 text-xs border border-dashed border-accent rounded-full bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent placeholder:text-ink-3"
            />
            <button
              onClick={() => dispatch({ type: 'CLEAR_SYNONYMS', groupIndex, rowIndex })}
              className="text-xs text-ink-3 hover:text-signal transition-colors"
              title="Clear all synonyms"
            >
              clear
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Render a single group ────────────────────────────────────────────

  const renderGroup = (group: GroupState, groupIndex: number) => {
    const isLastGroup = groupIndex === state.groups.length - 1;
    const groupContent = (
      <>
        <div className="space-y-2">
          {group.rows.map((row, rowIndex) => renderRow(row, groupIndex, rowIndex, group))}
        </div>

        {/* Group action line: add term / add group (last group) / proximity */}
        <div className="mt-3 flex flex-wrap gap-5 text-[13px] text-accent">
          {group.rows.length < 10 && (
            <button
              onClick={() => dispatch({ type: 'ADD_ROW', groupIndex })}
              disabled={isLoading}
              className="hover:text-accent-deep transition-colors disabled:opacity-50"
            >
              + Add term
            </button>
          )}
          {/* Boolean groups aren't supported for FDA Letters. */}
          {isLastGroup && !letterCorpus && state.groups.length < 4 && (
            <button
              onClick={() => dispatch({ type: 'ADD_GROUP' })}
              disabled={isLoading}
              className="hover:text-accent-deep transition-colors disabled:opacity-50"
            >
              + Add group
            </button>
          )}
          {/* Per-group Proximity Search — hidden for FDA Letters and Japanese
              text search (both unsupported). */}
          {!letterCorpus && !nativeText && (
            <button
              onClick={() => dispatch({ type: 'TOGGLE_PROXIMITY', groupIndex })}
              disabled={isLoading}
              aria-expanded={group.proximityEnabled}
              className="hover:text-accent-deep transition-colors"
            >
              Proximity search
            </button>
          )}
        </div>

        {!letterCorpus && !nativeText && group.proximityEnabled && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="text"
              value={group.proximity.term_a}
              onChange={(e) =>
                dispatch({ type: 'UPDATE_PROXIMITY', groupIndex, field: 'term_a', value: e.target.value })
              }
              onKeyDown={handleKeyDown}
              placeholder="Term A"
              disabled={isLoading}
              className="w-32 px-2.5 py-1.5 border border-line-input rounded-ctl focus:outline-none focus:ring-2 focus:ring-accent-ring focus:border-accent disabled:opacity-50 text-sm"
            />
            <span className="text-ink-3 text-xs">followed within</span>
            <select
              value={group.proximity.max_distance}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_PROXIMITY',
                  groupIndex,
                  field: 'max_distance',
                  value: parseInt(e.target.value, 10),
                })
              }
              disabled={isLoading}
              className="w-16 px-2 py-1.5 border border-line-input rounded-ctl bg-white focus:outline-none focus:ring-2 focus:ring-accent-ring text-sm"
            >
              {[1, 2, 3, 5, 10, 15, 20, 30, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="text-ink-3 text-xs">words by</span>
            <input
              type="text"
              value={group.proximity.term_b}
              onChange={(e) =>
                dispatch({ type: 'UPDATE_PROXIMITY', groupIndex, field: 'term_b', value: e.target.value })
              }
              onKeyDown={handleKeyDown}
              placeholder="Term B"
              disabled={isLoading}
              className="w-32 px-2.5 py-1.5 border border-line-input rounded-ctl focus:outline-none focus:ring-2 focus:ring-accent-ring focus:border-accent disabled:opacity-50 text-sm"
            />
          </div>
        )}
      </>
    );

    // When multiple groups, wrap each group in a bordered card
    if (hasMultipleGroups) {
      return (
        <div
          key={groupIndex}
          className="relative border border-line bg-white rounded p-4"
        >
          {/* Group header with label and remove button */}
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow">
              Group {groupIndex + 1}
            </span>
            <button
              onClick={() => dispatch({ type: 'REMOVE_GROUP', groupIndex })}
              disabled={isLoading}
              className="p-1 text-ink-3 hover:text-signal rounded-ctl transition-colors"
              title="Remove group"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {groupContent}
        </div>
      );
    }

    // Single group: no card wrapper
    return <div key={groupIndex}>{groupContent}</div>;
  };

  return (
    <div className="bg-white border border-line rounded">
      {/* Builder */}
      <div className="px-5 py-[18px] flex flex-col gap-3">
      {/* (A "Search across: Labels | Other Documents" mode toggle lived here.
          It let the user swap the whole query onto the supplementary corpora
          (SBA, EPAR, UK PIL, AU CMI, JP PMDA, FDA Letters).  Removed
          2026-07-29 — keyword search covers main labels only; FDA Letters
          remain reachable via the page-level Document Type control.  See
          docs/superpowers/specs/2026-07-29-keyword-search-remove-ha-documents-design.md */}

      {/* Groups */}
      <div className="space-y-0">
        {state.groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {/* Group operator between groups.
                D6: disabled when any row is negated (negated rows require AND). */}
            {groupIndex > 0 && (
              <div className="flex justify-center my-3">
                <button
                  onClick={() =>
                    dispatch({
                      type: 'SET_PRECEDING_OPERATOR',
                      groupIndex,
                      operator: group.precedingOperator === 'AND' ? 'OR' : 'AND',
                    })
                  }
                  disabled={isLoading || anyNegated}
                  title={anyNegated ? 'Exclude conditions combine with AND.' : undefined}
                  className="font-mono text-[12px] h-7 px-2.5 border border-line-input rounded-ctl bg-white text-ink hover:bg-panel transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {group.precedingOperator}
                </button>
              </div>
            )}
            {renderGroup(group, groupIndex)}
          </React.Fragment>
        ))}
      </div>

      {(jpMixedLanguage) && (
        <p
          className="flex flex-wrap items-center gap-x-1 text-xs text-warn-ink"
          aria-live="polite"
          data-testid="jp-ja-form-hint"
        >
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Use either Japanese or English terms in one search.</span>
        </p>
      )}
      </div>

      {filtersSlot != null && (
        <div className="px-5 py-3 border-t border-line-subtle">{filtersSlot}</div>
      )}

      {/* Footer bar: display-only QUERY line + Search */}
      <div className="pl-5 pr-3 py-3 border-t border-line-subtle bg-ground flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {queryLine && (
            <>
              <span className="font-mono text-[11px] tracking-[0.12em] text-ink-2">QUERY</span>
              <code data-testid="kw-query-line" className="font-mono text-[13px] text-accent-deep break-all">{queryLine}</code>
            </>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={isLoading || jpJaInvalid || fieldRowBlocked || noLiteralCore || outputGroups.length === 0}
          title={jpJaInvalid || fieldRowBlocked || noLiteralCore ? 'Fix the keyword search as shown above to search' : undefined}
          className="btn-primary h-10 px-5 text-[14px]"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default KeywordSearchForm;
